import React, { useRef, useState } from 'react';
import { Avatar, AutoComplete, Image, Form, Select, Tooltip, message } from 'antd';
import { UserAddOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';
import { workwiseServices } from '../../../Workw-Api/api';
import { useDocument } from '../../../Pages/SpreadsheetContainer/documentInfoContex';
import { emitAddMemebers } from '../../../socket';
import defaultProfileImage from '../../../assets/defult-profile-logo.png';
import './shareForm.css';
import PeopleWithAccess from './peopleWithAccess';

const { Option } = Select;

const ShareForm = ({ setIsModalVisible, setPrivacy, onBack, setSubmitting }) => {
    const { id } = useParams();
    const [rightType, setRightType] = useState(null);
    const [selectedMember, setSelectedMember] = useState({});
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [errors, setErrors] = useState({});
    const { documentInfo, setDocumentInfo } = useDocument();
    const debouncedGetAllSuggestions = useRef(
        debounce(async (payload) => {
            try {
                const res = await workwiseServices.getAllSuggestion(payload);
                const data = res.data.data;
                const filteredData = data.filter((member) =>
                    !documentInfo.members.some((docMember) => docMember.id === member.id)
                );

                const optionsData = filteredData.map((member) => ({
                    key: member.id,
                    value: member.name,
                    label: (
                        <div className="member-option" style={{ display: 'flex', gap: 8 }}>
                            <div>
                                <Avatar
                                    size={30}
                                    src={
                                        member.image ? (
                                            <Image src={member.image} />
                                        ) : (
                                            <Image
                                                src={defaultProfileImage}
                                                style={{ width: '30px' }}
                                            />
                                        )
                                    }
                                />
                            </div>
                            <div className="member-info">
                                <span className="name" style={{ fontWeight: '500' }}>
                                    {member.name}
                                </span>
                                <br />
                                <span className="designation">{member.designation}</span>
                            </div>
                        </div>
                    ),
                }));
                setOptions(optionsData);
            } catch (error) {
                message.error('something went wrong...');
            }
        }, 300)
    ).current;

    const handleSearch = (value) => {
        setInputValue(value);
        setErrors((prev) => ({ ...prev, person: null }));
        if (value !== '') {
            const payload = {
                notRequired: [],
                pageNo: 1,
                pageSize: 20,
                referenceId: '00000000-0000-0000-0000-000000000000',
                removeSelf: true,
                search: value,
                types: [1],
            }
            debouncedGetAllSuggestions(payload);
        }
        else {
            debouncedGetAllSuggestions.cancel();
            setOptions([]);
            setSelectedMember({});
        }
    };

    const handleSelect = (value) => {
        const selectedUser = options.find((user) => user.value === value);
        if (selectedUser) {
            setSelectedMember(selectedUser);
        }
    };

    const handleRights = (value) => {
        setErrors((prev) => ({ ...prev, rights: null }));
        if (value === 'collaborator') {
            setRightType(2);
        } else if (value === 'reader') {
            setRightType(1);
        }
    };
    const handleAddMember = async () => {
        const nextErrors = {};
        if (!selectedMember.key || selectedMember.value !== inputValue) {
            nextErrors.person = 'Pick a person from the suggestions.';
        }
        if (rightType === null) {
            nextErrors.rights = 'Choose a right.';
        }
        if (nextErrors.person || nextErrors.rights) {
            setErrors(nextErrors);
            return;
        }
        setErrors({});
        setSubmitting(true);
        const payload = [{
            id: documentInfo.referenceId,
            memberId: selectedMember.key,
            memberRightType: rightType
        }];
        try {
            await workwiseServices.addDocumentMember(documentInfo.referenceId, payload);
        } catch (e) {
            message.error("Something went wrong....");
            setSubmitting(false);
            return;
        }
        setIsModalVisible(false);
        // Tells the other collaborators' sockets to refetch the member list.
        emitAddMemebers(id);
        setPrivacy(0);
        message.success("Document shared successfully");

        const response = await workwiseServices.getDocumentById(id);
        if (response?.status == 200 && response.data?.data?.rightType !== 0 && response.data?.data) {
            setDocumentInfo(response.data.data);
        }
    };

    return (
        <div className="restricted-container">
            <div className="share-section-head">
                {onBack && (
                    <Tooltip title="Back to general access" placement="bottomLeft">
                        <button
                            type="button"
                            className="share-back"
                            aria-label="Back to general access"
                            onClick={onBack}
                        >
                            <ArrowLeftOutlined />
                        </button>
                    </Tooltip>
                )}
                <p className="share-section-label">Invite people</p>
            </div>
            <Form id="share-invite-form" className="share-form" layout="vertical" onFinish={handleAddMember}>
                <div className="share-fields">
                    <div className="share-field share-field--person">
                        <label className="share-field__label" htmlFor="share-person">
                            Person
                            <span className="share-field__req" aria-label="required">*</span>
                        </label>
                        <div className={`share-control ${errors.person ? "share-control--invalid" : ""}`}>
                            <UserAddOutlined className="share-control__icon" />
                            <Form.Item className="share-control__field">
                                <AutoComplete
                                    id="share-person"
                                    className="share-input"
                                    options={options}
                                    onSelect={handleSelect}
                                    onSearch={handleSearch}
                                    onChange={setInputValue}
                                    placeholder="Add people by name"
                                    notFoundContent={"No data"}
                                    dropdownStyle={{
                                        width: "auto",
                                        minWidth: options.length == 0 ? "auto" : "180px"
                                    }}
                                />
                            </Form.Item>
                        </div>
                        {errors.person && (
                            <span className="share-field__error" role="alert">{errors.person}</span>
                        )}
                    </div>
                    <div className="share-field share-field--rights">
                        <label className="share-field__label" htmlFor="share-rights">
                            Rights
                            <span className="share-field__req" aria-label="required">*</span>
                        </label>
                        <Form.Item className={`share-control__select ${errors.rights ? "share-control__select--invalid" : ""}`}>
                            <Select id="share-rights" onChange={handleRights} placeholder="Select" dropdownStyle={{ minWidth: "100px" }}>
                                <Option value="reader">Reader</Option>
                                <Option value="collaborator">Collaborator</Option>
                            </Select>
                        </Form.Item>
                        {errors.rights && (
                            <span className="share-field__error" role="alert">{errors.rights}</span>
                        )}
                    </div>
                </div>
            </Form>
            <PeopleWithAccess />
        </div>
    );
};

export default ShareForm;
